import { Injectable, inject } from '@angular/core';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { Customer } from '@df/session/customer/customer.interfaces';
import { Subject } from 'rxjs';
import { NSagepay } from '../sagepay.namespace';

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            sccp_store?: number | null;
        }
    }
}

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface ISuccessData {
            sccp_payment_reference?: number;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class SagepayCloudPaymentService {
    status = new Status();

    storeIdSubject = new Subject<number | null | undefined>();

    private _storeId?: number | null;
    protected onCustomerChange: any;

    protected app = inject(App);
    protected customerModel = inject(CustomerModel);
    protected requestService = inject(RequestService);
    protected timeoutService = inject(TimeoutService);

    constructor() {
        this.customerModel.obs.data.subscribe(() => this.onCustomerChange());

        this.onCustomerChange = this.timeoutService.debounce(() => this._onCustomerChange(), 100);

        this.onCustomerChange();
    }

    /**
     * Currently set cloud payment store id
     * @returns {number | null | undefined}
     */
    get storeId(): number | undefined | null {
        return this._storeId;
    }

    /**
     * Current cloud payment store id setter
     */
    set storeId(id: number | undefined | null) {
        const previous = this.storeId;
        this._storeId = id;

        if (previous !== id) {
            this.storeIdSubject.next(this.storeId);
        }
    }

    /**
     * Changes sagepay cloud payment store id
     * logouts customer if required
     * @param {Status}              status
     * @param {number | null}       storeId
     * @param {IRequestCallback}    onSuccess
     * @param {IRequestCallback}    onError
     */
    changeStore(storeId: number | null, onSuccess?: IRequestCallback, onError?: IRequestCallback): Promise<Customer.ILoadResponse | void> {
        return this.app.request.send<Customer.ILoadResponse | void>(
            this.status,
            () => {
                return new Promise<Customer.ILoadResponse | void>((resolve, reject) => {
                    if (storeId || storeId === null) {
                        if (storeId === this.storeId) {
                            resolve();
                        } else {
                            const setStore = () => {
                                this.setStore(undefined, { store_id: storeId }, resolve, reject);
                            };
                            if (this.customerModel.isLoggedIn) {
                                this.requestService.send(undefined, () => this.requestService.nexus.checkout.logout({}), setStore, reject);
                            } else {
                                setStore();
                            }
                        }
                    } else {
                        reject({ error: 'Please provide store id.' });
                    }
                });
            },
            onSuccess,
            onError
        );
    }

    /**
     * Sends renew payment request
     * @param {Status}                                  status
     * @param {NSagepay.ICloudPaymentRenewPaymentData}   data
     * @param {IRequestCallback}                        onSuccess
     * @param {IRequestCallback}                        onError
     */
    renewPayment(
        status: Status | undefined,
        data: NSagepay.ICloudPaymentRenewPaymentData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<NSagepay.ICloudPaymentRenewPaymentResponse> {
        return this.app.request.send(status, { path: NSagepay.Endpoints.CLOUD_PAYMENT_RENEW_PAYMENT, data }, onSuccess, onError);
    }

    /**
     * Sends set store request
     * @param {Status}                              status
     * @param {NSagepay.ICloudPaymentSetStoreData}   data
     * @param {IRequestCallback}                    onSuccess
     * @param {IRequestCallback}                    onError
     */
    protected setStore(
        status: Status | undefined,
        data: NSagepay.ICloudPaymentSetStoreData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Customer.ILoadResponse> {
        return this.app.request.send(status, { path: NSagepay.Endpoints.CLOUD_PAYMENT_SET_STORE, data }, onSuccess, onError);
    }

    /**
     * On customer change callback
     */
    private _onCustomerChange(): void {
        if (this.customerModel.data && !this.status.busy) {
            this.storeId = this.customerModel.data.sccp_store;
            if (this.customerModel.isLoggedIn && this.isSpcp()) {
                this.requestService.send(undefined, () => this.requestService.nexus.checkout.logout({}));
            }
        }
    }

    isSpcp(): boolean {
        return !!this.customerModel.data.sccp_store;
    }
}
